import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'

import './_newMessage.scss'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import { addToast } from 'components/Toast/Toast'
import { useHeaderLayout } from 'context/headerLayoutContext'
import { usePageTitle } from 'context/pageTitleContext'
import CreateMessage from 'features/Messaging/CreateMessage/CreateMessage'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { createMassMessageForGroup } from 'services/endpoints/message'
import type { IMessage } from 'types/interfaces/IMessage'
import type { IProgressInfo } from 'types/interfaces/ITypes'

import { Recipients } from '../components/Recipients'

interface AudienceUser {
  id: string
  avatar?: {
    url?: string | null
  }
}


const isGroupSelection = (value: any) =>
  value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  typeof value.id === 'string'

const isUsersSelection = (value: any) =>
  Array.isArray(value) &&
  (
    value.length === 0 ||
    typeof value[0]?.id === 'string'
  )
const flattenMessageIds = (
  value: string | string[] | null
) => Array.isArray(value) ? value.flat() : value

const getRecipientCount = (
  value: any
): number | null => {
  if (!value) return null

  if (Array.isArray(value)) {
    return value.length
  }

  if (typeof value === 'object') {
    if (Array.isArray(value.users)) {
      return value.users.length
    }

    if (Array.isArray(value.members)) {
      return value.members.length
    }

    if (Array.isArray(value.recipients)) {
      return value.recipients.length
    }

    if (typeof value.count === 'number') {
      return value.count
    }

    if (typeof value.total === 'number') {
      return value.total
    }
  }

  return null
}

export const NewMessage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setTitle } = usePageTitle()
  const { state } = useLocation() as any

  const [message, setMessage] = useState<any>({
    audience: '',
    text: '',
    audioMessage: null,
    audioPreview: null,
    price: 0,
    media: [],
    previewMedia: [],
    vaultImages: [],
  })

const [audienceUsers, setAudienceUsers] =
  useState<AudienceUser[] | null>(null)
  const [audienceIds, setAudienceIds] =
    useState<string[] | null>(null)
  const [groupId, setGroupId] =
    useState<string | null>(null)
  const { setHideFooter } = useHeaderLayout()

  const [messageId, setMessageId] =
    useState<string | string[] | null>(null)

  const [selectedGroup, setSelectedGroup] = useState({
    name: 'All',
    avatars: [] as string[],
  })
  const [selectedFiles, setSelectedFiles] = useState<any>([])
  const [progressInfos, setProgressInfos] = useState<{ val: IProgressInfo[] }>({ val: [] })
  const [
  selectedGroupUserCount,
  setSelectedGroupUserCount
] = useState<number | null>(null)

  const hasPreselectedAudience =
    Array.isArray(state?.audience) && state.audience.length > 0

  const preselectedAudience = useMemo(() => {
    if (!hasPreselectedAudience) return null

    const users = Array.isArray(state?.audience)
    ? state.audience
    : []
    return {
      kind: 'users' as const,
      users,
      name: 'Selected Users',
      avatars: users.map((u: any) => u?.avatar?.url).filter(Boolean),
      count: users.length,
    }
  }, [hasPreselectedAudience, state?.audience])

  const type = useMemo(() => {
    if (groupId) return 'mass message'
    if (audienceIds && audienceIds.length > 1) return 'mass message'
    return 'text'
  }, [groupId, audienceIds])

  const invalidRecipientReason = useMemo(() => {
    if (
      groupId &&
      selectedGroupUserCount === 0
    ) {
      return 'empty-group'
    }

    if (
      !groupId &&
      (!audienceIds || audienceIds.length === 0)
    ) {
      return 'no-audience'
    }

    return null
  }, [
    groupId,
    selectedGroupUserCount,
    audienceIds
  ])

  useEffect(() => {
    setTitle(t('newMessage'))
  }, [setTitle, t])

    useEffect(() => {
    setHideFooter(true)
    return () => setHideFooter(false)
  }, [setHideFooter])

  useEffect(() => {
    if (!hasPreselectedAudience) return

    const incomingUsers: AudienceUser[] = Array.isArray(state?.audience)
      ? state.audience
      : []

    const avatarsArray = incomingUsers
      .map((u: AudienceUser) => u?.avatar?.url)
      .filter(Boolean)

    setSelectedGroup({
      name: 'Selected Users',
      avatars: avatarsArray as string[],
    })

    setMessage((prev: any) => ({ ...prev, audience: incomingUsers }))
    setGroupId(null)
    setAudienceUsers(incomingUsers)
    setAudienceIds(incomingUsers.map((u: AudienceUser) => u.id))
    setSelectedGroupUserCount(incomingUsers.length)

    window.history.replaceState({}, document.title)
  }, [hasPreselectedAudience, state])

  useEffect(() => {
    const val = message?.audience ?? null

    if (isGroupSelection(val)) {
      setGroupId(val.id)
      setAudienceUsers(null)
      setAudienceIds(null)
      return
    }

    if (isUsersSelection(val)) {
      setGroupId(null)
      setAudienceUsers(val)
      setAudienceIds(
        val.map((u: AudienceUser) => u.id)
      )
      return
    }

    setGroupId(null)
    setAudienceUsers(null)
    setAudienceIds(null)
  }, [message?.audience])

  const handleSetAudience = useCallback((val: any) => {
    setMessage((prev: any) => ({ ...prev, audience: val }))
    setSelectedGroupUserCount(getRecipientCount(val))

    if (Array.isArray(val)) {
      const avatarsArray = val.map((u: any) => u?.avatar?.url).filter(Boolean)
      setSelectedGroup({
        name: 'Selected Users',
        avatars: avatarsArray,
      })
      return
    }

    if (val?.name) {
      setSelectedGroup({
        name: val.name,
        avatars: (val.avatars ?? []).filter(Boolean),
      })
      return
    }

    setSelectedGroup({
      name: 'All',
      avatars: [],
    })
  }, [])

  const addMessage = useMutation(
    async (newMessage: IMessage) => {
      const previousMessagesId = flattenMessageIds(messageId)
      const effectiveGroupId = groupId || null

      const userIds =
        !effectiveGroupId &&
        Array.isArray(audienceIds) &&
        audienceIds.length > 0
          ? audienceIds
          : null

      return await createMassMessageForGroup(
        newMessage,
        effectiveGroupId,
        previousMessagesId,
        type,
        { userIds }
      )
    },
    {
      onSuccess: (resp: any) => {
        const recipientCount = audienceIds?.length ?? 0
        const isSingleRecipient = !groupId && recipientCount === 1

        if (!isSingleRecipient) {
          navigate('/chat')
          return
        }

        const userId = audienceIds![0]
        const conversationId =
          resp?.data?.message?.conversation_id ??
          resp?.message?.conversation_id ??
          resp?.conversation_id ??
          null

        if (!conversationId) {
          navigate('/chat')
          return
        }

        const otherUserData = audienceUsers?.[0] ?? null
        navigate(`/chat/${userId}`, { state: { otherUserData, conversationId } })
      },
      onError: (err: any) => {
        const msg =
          err?.response?.data?.error?.message ?? t('error:errorSomethingWentWrong')
        addToast('error', msg)
      },
    }
  )

  const submitFn = (val: any) => {
    const media: any[] = []

    for (let i = 0; i < val.length; i++) {
      if (val[i].message?.message) {
        val[i].message.message.photos.forEach((photo: any) =>
          media.push({
            ...photo,
            percentage: 100,
            previewUrl: photo.url,
            storageUrl: photo.url,
            fileName: `shared-photo-${photo.id}`,
            type: 'photo',
          })
        )

        val[i].message.message.videos.forEach((video: any) => {
          const vaultableVideo = val[i].vaultable.videos.find((v: any) => v.id === video.id)
          media.push({
            ...video,
            percentage: 100,
            previewUrl: video.url,
            storageUrl: video.url,
            fileName: `shared-photo-${video.id}`,
            type: 'video',
            thumbnails: vaultableVideo?.thumbs.map((thumb: any) => thumb.src) || [],
            preview: vaultableVideo?.preview || false,
          })
        })
      }

      if (val[i].post) {
        val[i].post.photos.forEach((photo: any) =>
          media.push({
            ...photo,
            percentage: 100,
            previewUrl: photo.url,
            storageUrl: photo.url,
            fileName: `shared-photo-${photo.id}`,
            type: 'photo',
          })
        )

        val[i].post.videos.forEach((video: any) =>
          media.push({
            ...video,
            percentage: 100,
            previewUrl: video.url,
            storageUrl: video.url,
            fileName: `shared-photo-${video.id}`,
            type: 'video',
          })
        )
      }
    }

    const mergedMedia = ([] as any[]).concat.apply([], media)
    mergedMedia.forEach((item: any) => (item.percentage = 100))

    setProgressInfos(prev => ({ val: [...prev.val, ...mergedMedia] }))
    setSelectedFiles((prev: any) => [...prev, ...mergedMedia])
  }

  useLayoutEffect(() => {
      const viewport = document.querySelector<HTMLMetaElement>(
          'meta[name="viewport"]'
      )

      if (!viewport) return

      const previousContent = viewport.getAttribute('content') ?? ''

      viewport.setAttribute(
          'content',
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, interactive-widget=resizes-content'
      )

      return () => {
          viewport.setAttribute('content', previousContent)
      }
  }, [])
useEffect(() => {
  if (window.innerWidth >= 380) return

  const visualViewport = window.visualViewport
  if (!visualViewport) return

  const initialViewportHeight = visualViewport.height

  const setKeyboardOpen = (open: boolean) => {
    document.body.classList.toggle('header-hidden', open)

    document
      .querySelector('.whs__header')
      ?.classList.toggle('header-hidden', open)
  }

  const onViewportResize = () => {
    const keyboardOpen =
      initialViewportHeight - visualViewport.height > 120

    setKeyboardOpen(keyboardOpen)
  }

  visualViewport.addEventListener('resize', onViewportResize)

  return () => {
    visualViewport.removeEventListener('resize', onViewportResize)
    setKeyboardOpen(false)
  }
}, [])
  
  return (
    <WithHeaderSection
      headerSection={
        <Recipients
          setAudience={handleSetAudience}
          hasPreselectedAudience={hasPreselectedAudience}
          preselectedAudience={preselectedAudience}
          avatarSize="small"
        />
      }
      withoutBorder={true}
      customClass="whsNewMessage"
    >
      <div className="newMessage">
        <CreateMessage
          invalidRecipientReason={invalidRecipientReason}
          selectedRecipients={audienceIds}
          selectedGroupId={groupId}
          submitFn={submitFn}
          progressInfos={progressInfos}
          setProgressInfos={setProgressInfos}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          sendMessage={async (val: any) => {
            if (!!groupId || (audienceIds?.length ?? 0) > 0) {
              return await addMessage.mutateAsync(val)
            }

            addToast('error', 'Select audience')
            return await Promise.reject(new Error('Select audience'))
          }}
          setMessageId={setMessageId}
          customClass="newMessage__create"
          writeMessageCustomClass="newMessage__writemessage"
        />
      </div>
    </WithHeaderSection>
  )
}